import axios from "axios";
import React, { Fragment, useEffect, useState, useRef } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { impersonate } from "../../../utils/impersonate";
import Footer from "../../layout/Footer";
import Loader from "../../layout/Loader";
import Menu from "../../layout/Menu";
import AddCompany from "./AddCompany";
import UpdateCompany from "./UpdateCompany";
import ExportCompanyModal from "./ExportCompanyModal";
import { buildCompanyListRequest } from "./companyExport";
import { ADMIN_JOINED_FROM, BILLING_MODE, BILLING_MODE_LABEL, COMPANY_SUBSCRIPTION_STATUS, formatDate } from "../../../constants";

const Companies = () => {
  const [users, setUsers] = useState([]);
  const [displayUsers, setDisplayUsers] = useState([]);
  // Pay-As-You-Go usage for the companies on THIS page, keyed by company id. Fetched with
  // one bulk request rather than one per row.
  const [paygUsage, setPaygUsage] = useState({});
  const paygUsageRequestRef = useRef(0);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Current-period usage for the PAYG companies on this page.
   *
   * One request for the whole page - the endpoint aggregates over the ledger with a single
   * $group - rather than one request per row. Failure is non-fatal: the Billing column
   * falls back to showing the mode without a figure, which is better than failing the
   * whole company list over a secondary number.
   */
  const fetchPaygUsage = async (companies) => {
    // A request id, because this is fired without await from getData. Fast tab/page/search
    // changes could otherwise land an older response over a newer page's data, showing one
    // company's topo count against another's row.
    const requestId = ++paygUsageRequestRef.current;
    const paygIds = companies
      .filter((company) => company.billingMode === BILLING_MODE.PAYG)
      .map((company) => company._id || company.id)
      .filter(Boolean);

    if (!paygIds.length) {
      if (requestId === paygUsageRequestRef.current) setPaygUsage({});
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_base_url}api/masterAdmin/payg-usage-summary`,
        { auth_code: process.env.REACT_APP_AUTH_CODE, companyIds: paygIds },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data.success === true && requestId === paygUsageRequestRef.current) {
        const byId = {};
        (response.data.data || []).forEach((row) => {
          byId[row.companyId] = row;
        });
        setPaygUsage(byId);
      }
    } catch (error) {
      console.error("Error fetching Pay-As-You-Go usage:", error);
    }
  };

  const [alert, setAlert] = useState(false);
  const [alert2, setAlert2] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [changed, setChanged] = useState(false);
  const [updateId, setUpdateId] = useState();
  const rows = [20, 50, 100];
  const [userPerPage, setUserPerPage] = useState(rows[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [managerModal, setManagerModal] = useState(false);
  const [managerModal1, setManagerModal1] = useState(false);
  const [exportModal, setExportModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [activeTab, setActiveTab] = useState("active"); // "active", "deleted", "legacy", "signupIssue"
  const [searchTerm, setSearchTerm] = useState("");
  const [joinedFromFilter, setJoinedFromFilter] = useState("ALL");
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [addOnStats, setAddOnStats] = useState(null);
  const [restartingId, setRestartingId] = useState(null);
  const [signupIssueActionId, setSignupIssueActionId] = useState(null);

  useEffect(() => {
    document.getElementById("page-loader").style.display = "none";
    getUserData();
    // getAddOnStats();
    var element = document.getElementById("page-container");
    element.classList.add("show");
  }, []);

  useEffect(() => {
    getUserData();
  }, [activeTab, currentPage, userPerPage, searchTerm, joinedFromFilter]);

  const getUserData = async () => {
    try {
      setIsLoading(true);

      // Shared with the CSV export so the two can never diverge on
      // endpoint or params — see ./companyExport.js
      const { url: myurl, requestData } = buildCompanyListRequest({
        tab: activeTab,
        page: currentPage,
        limit: userPerPage,
        searchTerm,
        joinedFrom: joinedFromFilter,
      });

      const response = await axios.post(myurl, requestData, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.data.success === true) {
        const { data, pagination } = response.data;
        const arr = data.map((elem, index) => {
          const globalIndex = (currentPage - 1) * userPerPage + index + 1;
          return { ...elem, indexs: globalIndex };
        });

        setUsers(arr);
        setDisplayUsers(arr);
        fetchPaygUsage(arr);
        setTotalPages(pagination?.totalPages || 0);
        setTotalCount(pagination?.totalCount || 0);
      } else {
        toast.error(response.data.message || "Failed to fetch company data");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(error.response?.data?.message || "Failed to fetch company data");
    } finally {
      setIsLoading(false);
    }
  };

  const getAddOnStats = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_base_url}api/masterAdmin/addon-usage-stats`,
        {
          headers: { auth_code: process.env.REACT_APP_AUTH_CODE }
        }
      );

      if (response.data.success) {
        setAddOnStats(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching add-on stats:", error);
    }
  };

  const handleDeleteUser = (id) => {
    setAlert(true);
    setDeleteId(id);
  };

  // Only auto-pay (Automated) companies can restart a subscription, and only when the
  // current subscription is missing/canceled/expired. Manual-billing companies are excluded.
  const canRestartSubscription = (elem) => {
    // Pay-As-You-Go companies have no seat subscription to restart (the backend refuses it).
    if (elem.billingMode === BILLING_MODE.PAYG) return false;
    if (!elem.validPaymentMethodRequired) return false;
    const status = elem?.currentSubscription?.status;
    return !status || ["incomplete_expired", "canceled"].includes(status);
  };

  // A website sign-up created the company but the card/Stripe step failed (admin.signupIssue).
  const hasOpenSignupIssue = (elem) =>
    Boolean(elem?.signupIssue?.stage) && !elem.signupIssue.resolvedAt;

  const runSignupIssueAction = async (elem, endpoint, confirmMessage) => {
    if (!window.confirm(confirmMessage)) return;

    setSignupIssueActionId(elem.id);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_base_url}api/masterAdmin/${endpoint}`,
        { auth_code: process.env.REACT_APP_AUTH_CODE, companyId: elem.id }
      );
      if (response.data.success) {
        toast.success(response.data.message || "Done.");
      } else {
        toast.error(response.data.message);
      }
      getUserData(); // refresh list
    } catch (err) {
      toast.error(err.response?.data?.message || "Action failed.");
    } finally {
      setSignupIssueActionId(null);
    }
  };

  const handleRetrySignupSetup = (elem) =>
    runSignupIssueAction(
      elem,
      "retry-signup-setup",
      "Retry the Stripe setup? This finds or creates the Stripe customer, creates the subscription and emails a new payment link."
    );

  const handleResolveSignupIssue = (elem) =>
    runSignupIssueAction(
      elem,
      "resolve-signup-issue",
      "Mark this sign-up issue as resolved? Only do this once the company's card situation is sorted out."
    );

  const handleRestartSubscription = async (elem) => {
    const confirmMessage =
      "Restart the subscription? This recreates the Stripe subscription and emails a new payment link.";
    if (!window.confirm(confirmMessage)) return;

    setRestartingId(elem.id);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_base_url}api/masterAdmin/restart-subscription`,
        { auth_code: process.env.REACT_APP_AUTH_CODE, id: elem.id }
      );
      if (response.data.success) {
        toast.success(response.data.message || "Subscription restarted.");
        getUserData(); // refresh list
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Action failed.");
    } finally {
      setRestartingId(null);
    }
  };

  const DeleteUser = async () => {
    try {
      const myurl = `${process.env.REACT_APP_base_url}api/masterAdmin/delete-admin`;
      const requestData = {
        auth_code: process.env.REACT_APP_AUTH_CODE,
        id: deleteId,
      };

      const response = await axios.post(myurl, requestData, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.data.success === true) {
        setDeleteId("");
        setAlert(false);
        setAlert2(true);
        setAlertMessage(response.data.message || "User deleted successfully.");
      } else {
        toast.error(response.data.message || "Failed to delete user");
      }
    } catch (error) {
      if (error.response) {
        const status = error.response.status;

        if (status >= 400 && status < 500) {
          toast.error(error.response.data?.message || "Client error occurred");
        } else if (status >= 500) {
          toast.error("Something went wrong on the server");
        } else {
          toast.error("An unexpected error occurred");
        }
      } else {
        toast.error("Network error or server did not respond");
      }

      console.error("Error details:", error);
    }
  };

  const toggleLegacyStatus = async (companyId, currentStatus) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_base_url}api/masterAdmin/toggle-legacy-status`,
        {
          auth_code: process.env.REACT_APP_AUTH_CODE,
          companyId: companyId,
          legacyStatus: !currentStatus,
          notes: !currentStatus ? 'Legacy status granted by Super Admin' : 'Legacy status removed by Super Admin'
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      if (response.data.success) {
        toast.success(`Company ${!currentStatus ? 'marked as legacy' : 'legacy status removed'} successfully!`);
        getUserData(); // Refresh the list
      } else {
        toast.error(response.data.message || "Failed to update legacy status");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error updating legacy status");
    }
  };

  const handleUserLogin = (id) => {
    impersonate({
      endpoint: "company-login",
      params: { userId: id },
      baseUrl: process.env.REACT_APP_COMPANY_URL,
    });
  };

  const handleCompanyUserLogin = (id) => {
    impersonate({
      endpoint: "company-user-login",
      params: { company: id },
      baseUrl: process.env.REACT_APP_COMPANY_USER_URL,
    });
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleFilterChange = (e) => {
    setJoinedFromFilter(e.target.value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1); // Reset to first page when changing tabs
    setSearchTerm(""); // Clear search when switching tabs
    setJoinedFromFilter("ALL"); // Reset filter when switching tabs
  };

  const paginate = (num) => {
    setCurrentPage(num);
  };

  const renderPagination = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    if (currentPage > 1) {
      pageNumbers.push(
        <li key="prev" className="page-item">
          <span className="page-link" onClick={() => paginate(currentPage - 1)} style={{ cursor: "pointer" }}>
            Previous
          </span>
        </li>
      );
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <li key={i} className={currentPage === i ? "page-item active" : "page-item"}>
          <span className="page-link" onClick={() => paginate(i)} style={{ cursor: "pointer" }}>
            {i}
          </span>
        </li>
      );
    }

    // Next button
    if (currentPage < totalPages) {
      pageNumbers.push(
        <li key="next" className="page-item">
          <span className="page-link" onClick={() => paginate(currentPage + 1)} style={{ cursor: "pointer" }}>
            Next
          </span>
        </li>
      );
    }

    return pageNumbers;
  };

  const renderAddOnUsage = (elem) => {
    if (!elem.addOnCounts) return <span className="text-muted">-</span>;

    const { foundationAnalysisCount = 0, floorLevelCount = 0 } = elem.addOnCounts;

    if (foundationAnalysisCount === 0 && floorLevelCount === 0) {
      return <span className="text-muted">None</span>;
    }

    return (
      <div>
        {foundationAnalysisCount > 0 && (
          <>
            <span className="badge bg-primary me-1">
              Foundation: {" "}{foundationAnalysisCount}
              {elem.legacyCompany && <small> (FREE)</small>}
            </span>
            <br />
          </>
        )}
        {floorLevelCount > 0 && (
          <span className="badge bg-info me-1">
            Floor: {" "} {floorLevelCount}
          </span>
        )}
      </div>
    );
  };

  const renderLegacyStatus = (elem) => {
    return (
      <div className="d-flex align-items-center">
        {elem.legacyCompany ? (
          <span className="badge bg-warning me-2">
            <i className="fa fa-star me-1 mr-2"></i>Legacy
          </span>
        ) : (
          <span className="badge bg-secondary me-2">Standard</span>
        )}

        {/* TODO: COMMENT AS NOT LOOKING GOOD */}
        {/* 
        {elem.status === 1 && (
          <button
            className={`btn btn-sm ${elem.legacyCompany ? 'btn-outline-warning' : 'btn-outline-success'}`}
            onClick={() => toggleLegacyStatus(elem.id, elem.legacyCompany)}
            title={elem.legacyCompany ? 'Remove legacy status' : 'Grant legacy status'}
          >
            <i className={`fa ${elem.legacyCompany ? 'fa-star' : 'fa-star'}`}></i>
          </button>
        )} */}
      </div>
    );
  };

  useEffect(() => {
    if (changed) {
      getUserData();
      getAddOnStats();
      setChanged(false);
    }
  }, [changed]);

  return (
    <Fragment>
      <Loader />
      <div
        id="page-container"
        className="fade page-sidebar-fixed page-header-fixed"
      >
        <Menu />
        <AddCompany
          activeModal={managerModal}
          setActiveModal={() => setManagerModal(false)}
          setUpdateModal={() => setChanged(true)}
        />
        <UpdateCompany
          activeModal={managerModal1}
          setActiveModal={() => setManagerModal1(false)}
          setUpdateModal={() => setChanged(true)}
          handler={managerModal1}
          data={updateId}
        />
        <ExportCompanyModal
          activeModal={exportModal}
          setActiveModal={() => setExportModal(false)}
          defaultTab={activeTab}
          searchTerm={searchTerm}
          joinedFromFilter={joinedFromFilter}
          pageScope={{
            tab: activeTab,
            currentPage,
            pageSize: userPerPage,
            totalPages,
            totalCount,
          }}
        />
        <div id="content" className="content">
          <ol className="breadcrumb float-xl-right">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb-item active">Companies</li>
          </ol>
          <h1 className="page-header">
            Companies{" "}
            <div
              className="btn btn-success"
              onClick={() => {
                setChanged(false);
                setManagerModal(true);
              }}
            >
              <i className="fa fa-plus"></i>
            </div>
          </h1>

          {/* Add-On Usage Statistics */}
          {/* {addOnStats && (
            <div className="row mb-4">
              <div className="col-md-12">
                <div className="card">
                  <div className="card-header">
                    <h5 className="card-title mb-0">
                      <i className="fa fa-chart-bar me-2"></i>
                      Add-On Usage Statistics
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-3">
                        <div className="text-center">
                          <h4 className="text-primary">{addOnStats.overall.foundationAnalysisUsers}</h4>
                          <small>Foundation Analysis Users</small>
                          <div className="text-muted">
                            {addOnStats.overall.foundationAnalysisPercentage}% of total
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="text-center">
                          <h4 className="text-info">{addOnStats.overall.floorLevelUsers}</h4>
                          <small>Floor Level Users</small>
                          <div className="text-muted">
                            {addOnStats.overall.floorLevelPercentage}% of total
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="text-center">
                          <h4 className="text-success">{addOnStats.overall.bothAddOnsUsers}</h4>
                          <small>Both Add-Ons</small>
                          <div className="text-muted">
                            {addOnStats.overall.bothAddOnsPercentage}% of total
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="text-center">
                          <h4 className="text-dark">{addOnStats.overall.totalUsers}</h4>
                          <small>Total Users</small>
                          <div className="text-muted">
                            Across all companies
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )} */}

          {/* Tab Navigation */}
          <div className="card">
            <div className="card-header">
              <ul className="nav nav-tabs card-header-tabs">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "active" ? "active" : ""}`}
                    onClick={() => handleTabChange("active")}
                    style={{
                      border: "none",
                      background: "none",
                      color: activeTab === "active" ? "#007bff" : "#6c757d"
                    }}
                  >
                    <i className="fa fa-building me-1 mr-2"></i>
                    Active Companies ({activeTab === "active" ? totalCount : "-"})
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "legacy" ? "active" : ""}`}
                    onClick={() => handleTabChange("legacy")}
                    style={{
                      border: "none",
                      background: "none",
                      color: activeTab === "legacy" ? "#007bff" : "#6c757d"
                    }}
                  >
                    <i className="fa fa-star me-1 mr-2"></i>
                    Legacy Companies ({activeTab === "legacy" ? totalCount : "-"})
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "signupIssue" ? "active" : ""}`}
                    onClick={() => handleTabChange("signupIssue")}
                    style={{
                      border: "none",
                      background: "none",
                      color: activeTab === "signupIssue" ? "#dc3545" : "#6c757d"
                    }}
                  >
                    <i className="fa fa-credit-card me-1 mr-2"></i>
                    Card Setup Failed ({activeTab === "signupIssue" ? totalCount : "-"})
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "deleted" ? "active" : ""}`}
                    onClick={() => handleTabChange("deleted")}
                    style={{
                      border: "none",
                      background: "none",
                      color: activeTab === "deleted" ? "#007bff" : "#6c757d"
                    }}
                  >
                    <i className="fa fa-trash me-1 mr-2"></i>
                    Deleted Companies ({activeTab === "deleted" ? totalCount : "-"})
                  </button>
                </li>
              </ul>
            </div>

            <div className="card-body">
              <div className="form-outline mb-4" style={{ display: "flex", gap: "10px" }}>
                {activeTab !== "legacy" && (
                  <select
                    className="form-select"
                    onChange={handleFilterChange}
                    value={joinedFromFilter}
                    style={{
                      padding: "5px 10px",
                      border: "1px solid #d5dbe0",
                      borderRadius: "4px",
                    }}
                  >
                    {Object.entries(ADMIN_JOINED_FROM).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))}
                  </select>
                )}
                <input
                  type="search"
                  className="form-control"
                  id="datatable-search-input"
                  placeholder={`Search ${activeTab === "legacy" ? "Legacy " : ""}Companies`}
                  value={searchTerm}
                  onChange={handleSearch}
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ whiteSpace: "nowrap" }}
                  onClick={() => setExportModal(true)}
                  disabled={isLoading}
                  title="Export companies to CSV"
                >
                  <i className="fa fa-download me-2 mr-2"></i>
                  Export
                </button>
              </div>

              <div className="row">
                <div className="col-12">
                  <div className="table-responsive">
                    <table id="product-listing" className="table">
                      <thead>
                        <tr>
                          <th>Sr. No.</th>
                          <th>Company Info</th>
                          <th>Billing</th>
                          <th>Payment Mode</th>
                          <th>Legacy Status</th>
                          <th>Trial Mode</th>
                          <th>Subscription Status</th>
                          <th>Add-On Usage</th>
                          <th>User Count</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading ? (
                          <tr className="text-center">
                            <td colSpan={11}>
                              <i
                                className="fa fa-spinner fa-spin"
                                style={{ fontSize: "20px" }}
                              ></i>{" "}
                              Loading...
                            </td>
                          </tr>
                        ) : displayUsers.length > 0 ? (
                          displayUsers.map((elem, index) => (
                            <tr key={index}>
                              <td>{elem.indexs}</td>
                              <td>
                                <div>
                                  <strong>{elem.username}</strong>
                                  {elem.legacyCompany && (
                                    <span className="badge bg-warning ml-2 ms-1">
                                      <i className="fa fa-star"></i>
                                    </span>
                                  )}
                                  {hasOpenSignupIssue(elem) && (
                                    <span
                                      className="badge bg-danger ml-2 ms-1"
                                      title={`Website sign-up: payment setup failed at ${elem.signupIssue.stage}${elem.signupIssue.message ? ` - ${elem.signupIssue.message}` : ""}`}
                                    >
                                      <i className="fa fa-credit-card me-1 mr-1"></i>Card setup failed
                                    </span>
                                  )}
                                  <br />
                                  <small className="text-muted">{elem.email}</small>
                                  <br />
                                  {elem.trialPeriodEndDate &&
                                    !(elem.billingMode === BILLING_MODE.PAYG && !elem.trialModeEnabled) && (
                                    <small className="text-info">
                                      Trial ends: {formatDate(elem.trialPeriodEndDate)}
                                    </small>
                                  )}
                                </div>
                              </td>
                              <td>
                                {/* Same wrapper + badge markup for both modes; only the colour differs
                                    (coral PAYG / grey Seat) so the modes stay distinguishable. */}
                                <div>
                                  <span className={`badge ${elem.billingMode === BILLING_MODE.PAYG ? 'bg-primary' : 'bg-secondary'}`}>
                                    {elem.billingMode === BILLING_MODE.PAYG ? BILLING_MODE_LABEL.PAYG : BILLING_MODE_LABEL.SEAT}
                                  </span>
                                  {elem.billingMode === BILLING_MODE.PAYG && paygUsage[elem._id || elem.id] && (
                                    <div className="mt-1">
                                      <small
                                        className="text-muted"
                                        title={`base $${Number(paygUsage[elem._id || elem.id].baseCharge || 0).toFixed(2)} + usage $${Number(paygUsage[elem._id || elem.id].usageAmount || 0).toFixed(2)}`}
                                      >
                                        {paygUsage[elem._id || elem.id].topoCount} topo
                                        {paygUsage[elem._id || elem.id].topoCount === 1 ? '' : 's'}
                                        {' · '}
                                        ${(paygUsage[elem._id || elem.id].estimatedTotal || 0).toFixed(2)} est.
                                      </small>
                                      {elem.trialModeEnabled === true &&
                                        paygUsage[elem._id || elem.id].trialUsage?.amount > 0 && (
                                          <small className="text-muted d-block">
                                            trial usage ${Number(paygUsage[elem._id || elem.id].trialUsage.amount).toFixed(2)} (not billed)
                                          </small>
                                        )}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td>
                                <div>
                                  {elem.validPaymentMethodRequired === true ? (
                                    <span className="badge bg-success">Automatic</span>
                                  ) : (
                                    <span className="badge bg-secondary">Manual</span>
                                  )}
                                  {elem.validPaymentMethodRequired && elem.cancelAtPeriodEnd && (
                                    <div className="mt-1">
                                      <span className="badge bg-warning">Cancelling</span>
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td>{renderLegacyStatus(elem)}</td>
                              <td>
                                {elem.trialModeEnabled === true ? (
                                  <span className="badge bg-info">Active</span>
                                ) : (
                                  <span className="badge bg-secondary">Disabled</span>
                                )}
                              </td>
                              <td>
                                {elem.validPaymentMethodRequired === true ? (
                                  (() => {
                                    // A PAYG company whose trial is off must not show "Trial",
                                    // even if an old subscription row still says trialing.
                                    const rawStatus = elem?.currentSubscription?.status;
                                    const subStatus =
                                      rawStatus === 'trialing' &&
                                      elem.billingMode === BILLING_MODE.PAYG &&
                                      !elem.trialModeEnabled
                                        ? 'active'
                                        : rawStatus;
                                    return (
                                      <span className={`badge ${subStatus === 'active' ? 'bg-success' :
                                        subStatus === 'trialing' ? 'bg-info' :
                                          subStatus === 'past_due' ? 'bg-warning' : 'bg-danger'
                                        }`}>
                                        {(COMPANY_SUBSCRIPTION_STATUS[subStatus] || 'Unknown').toUpperCase()}
                                      </span>
                                    );
                                  })()
                                ) : (
                                  <span className="badge bg-secondary">Manual Billing</span>
                                )}
                                {/* For a PAYG company adminsList falls through to
                                    moment().add(1,'month').startOf('month') - i.e. always the
                                    1st - which is wrong for any invoiceDayOfMonth of 2-5. The
                                    bulk usage response carries the real date, so prefer it. */}
                                {(paygUsage[elem._id || elem.id]?.nextInvoiceDate || elem?.nextInvoiceDate) && (
                                  <div className="mt-1">
                                    <small className="text-muted">
                                      {elem.billingMode === BILLING_MODE.PAYG ? 'Next invoice' : 'Next'}: {formatDate(
                                        paygUsage[elem._id || elem.id]?.nextInvoiceDate ||
                                          elem.nextInvoiceDate
                                      )}
                                    </small>
                                  </div>
                                )}
                              </td>
                              <td>{renderAddOnUsage(elem)}</td>
                              <td>
                                <div>
                                  <strong>{elem?.activeUserCount || 0}</strong>
                                  <small className="text-muted"> / {elem?.paidUserCount || 0}</small>
                                  <br />
                                  <small className="text-muted">Active / Paid</small>
                                </div>
                              </td>
                              <td>
                                {elem.status === 1 ? (
                                  <span className="badge bg-success">Active</span>
                                ) : (
                                  <span className="badge bg-danger">Deleted</span>
                                )}
                              </td>
                              <td>
                                {elem.status === 1 && (
                                  <div className="btn-group" role="group">
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => handleUserLogin(elem.id)}
                                      title="Company Panel"
                                    >
                                      <i className="fa fa-globe"></i>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-secondary"
                                      onClick={() => handleCompanyUserLogin(elem.id)}
                                      title="User Panel"
                                    >
                                      <i className="fa fa-user"></i>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-success"
                                      onClick={() => {
                                        setUpdateId(elem);
                                        setChanged(false);
                                        setManagerModal1(true);
                                      }}
                                      title="Edit Company"
                                    >
                                      <i className="fa fa-edit"></i>
                                    </button>
                                    {hasOpenSignupIssue(elem) && (
                                      <>
                                        <button
                                          className="btn btn-sm btn-outline-primary"
                                          onClick={() => handleRetrySignupSetup(elem)}
                                          disabled={signupIssueActionId === elem.id}
                                          title="Retry sign-up payment setup (re-sends the payment link)"
                                        >
                                          <i className={`fa ${signupIssueActionId === elem.id ? "fa-spinner fa-spin" : "fa-credit-card"}`}></i>
                                        </button>
                                        <button
                                          className="btn btn-sm btn-outline-success"
                                          onClick={() => handleResolveSignupIssue(elem)}
                                          disabled={signupIssueActionId === elem.id}
                                          title="Mark sign-up issue as resolved"
                                        >
                                          <i className="fa fa-check"></i>
                                        </button>
                                      </>
                                    )}
                                    {canRestartSubscription(elem) && (
                                      <button
                                        className="btn btn-sm btn-outline-warning"
                                        onClick={() => handleRestartSubscription(elem)}
                                        disabled={restartingId === elem.id}
                                        title="Restore Subscription"
                                      >
                                        <i className={`fa ${restartingId === elem.id ? "fa-spinner fa-spin" : "fa-redo"}`}></i>
                                      </button>
                                    )}
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleDeleteUser(elem.id)}
                                      title="Delete Company"
                                    >
                                      <i className="fa fa-trash"></i>
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr className="text-center">
                            <td colSpan={11}>No Record Found..</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="gt-pagination" style={{ display: "flex", justifyContent: "space-between" }}>
                    <ul className="pagination">
                      {renderPagination()}
                    </ul>
                    <div className="pagination-info" style={{ display: "flex", alignItems: "center", gap: "20px" }}>
                      <span style={{ fontSize: "14px", color: "#6c757d" }}>
                        Showing {((currentPage - 1) * userPerPage) + 1} to {Math.min(currentPage * userPerPage, totalCount)} of {totalCount} entries
                      </span>
                      <div
                        className="filter-pages"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <label htmlFor="selection" style={{ marginBottom: "0" }}>
                          Companies Per Page :
                        </label>
                        <select
                          className="selection"
                          style={{
                            outline: "0",
                            borderWidth: "0 0 1px",
                            borderColor: "black",
                            marginLeft: "10px",
                            cursor: "pointer",
                          }}
                          value={userPerPage}
                          onChange={(e) => {
                            setUserPerPage(Number(e.target.value));
                            setCurrentPage(1);
                          }}
                        >
                          {rows.map((value) => (
                            <option key={value} value={value}>{value}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {alert === true ? (
          <SweetAlert
            warning
            showCancel
            confirmBtnText="Yes, delete it!"
            confirmBtnBsStyle="danger"
            title="Are you sure?"
            onConfirm={DeleteUser}
            onCancel={() => {
              setAlert(false);
              setDeleteId("");
            }}
          >
            You will not be able to recover this Company!
          </SweetAlert>
        ) : (
          ""
        )}
        {alert2 && (
          <SweetAlert
            success
            title="Company Deleted Successfully!"
            onConfirm={() => {
              setAlert2(false);
              setAlertMessage("");
              getUserData();
            }}
          >
            <p>{alertMessage}</p>
          </SweetAlert>
        )}
        <Footer />
      </div>
    </Fragment>
  );
};

export default Companies;